import React, { Fragment, useState, useMemo } from 'react'
import { COUNTDOWN } from '..'
import { useForm } from 'react-hook-form'
import { verifyOtp, resendOtp } from '@lib/util/customer-handle'
import Input from '@modules/common/components/input'
import { Button, Flex, Radio, RadioGroup, Stack, Text } from '@chakra-ui/react'
import Countdown, { CountdownApi } from 'react-countdown'
import { LOCAL_PHONE_VERIFY } from '@constants/local-storage'
import { useAlert } from '@common/hooks'

interface OtpPropsType {
  phone: string
  countdownOtp: number
  onSetOtpVerified: (input: boolean) => void
}

interface OtpCredentials {
  phone?: string
  otp?: string
}

enum OTPChannel {
  SMS = 'sms',
  WHATSAPP = 'whatsapp'
}

const Otp: React.FC<OtpPropsType> = ({
  phone,
  countdownOtp,
  onSetOtpVerified
}) => {
  const [authError, setAuthError] = useState<string | undefined>(undefined)
  const [loading, setLoading] = useState(false)
  const [resending, setResending] = useState(false)
  const [channel, setChannel] = useState<string>(OTPChannel.SMS)
  const [countdown, setCountdown] = useState(countdownOtp)
  const [isResend, setIsResend] = useState(false)

  const { errorToast, successToast } = useAlert()

  const handleStartClick = (): void => {
    countdownApi && countdownApi.start()
  }

  const handlePauseClick = (): void => {
    countdownApi && countdownApi.pause()
  }

  let countdownApi: CountdownApi | null = null

  const { register, handleSubmit } = useForm<OtpCredentials>({
    defaultValues: {
      phone
    }
  })

  const onResendOtp = () => {
    setAuthError('')
    setResending(true)

    const phone = localStorage.getItem(LOCAL_PHONE_VERIFY)

    resendOtp({ phone: phone as string, channel })
      .then(({ data }) => {
        setCountdown(COUNTDOWN)
        setResending(false)
        setIsResend(!isResend)
        successToast({
          title: 'Resend Successfully',
          description: `A new OTP has been sent to your phone`
        })
      })
      .catch((e) => {
        errorToast({
          title: 'Error',
          description: 'Cant resend OTP'
        })
        setResending(false)
      })
  }

  const onSubmit = handleSubmit(async (input) => {
    setAuthError('')
    setLoading(true)
    handlePauseClick()

    const phone = localStorage.getItem(LOCAL_PHONE_VERIFY)

    if (input.otp?.length !== 6 && Number.isNaN(input.otp)) {
      setAuthError('OTP must be 6 digits')
      return
    }

    try {
      const { data } = await verifyOtp({ phone: phone as any, otp: input.otp })
      if (data.status === 'approved' && data.valid === true) {
        onSetOtpVerified(true)
        setCountdown(0)
        setLoading(false)
      } else {
        setLoading(false)
        setAuthError('Invalid or expired OTP')
        handleStartClick()
      }
    } catch (e) {
      setAuthError('Invalid or expired OTP')
      setLoading(false)
      handleStartClick()
    }
  })

  const renderer = ({ hours, minutes, seconds, completed }: any) => {
    if (completed) {
      setCountdown(0)
      return (
        <Text fontSize={'16px'} color={'red'}>
          OTP expired, please click button resend below to receive new OTP
        </Text>
      )
    } else {
      return (
        <Flex gap={2}>
          <Text fontSize={'16px'} color={'red'}>
            OTP expired after
          </Text>
          <Flex>
            <Text fontSize={'16px'} color={'red'}>
              {parseInt(minutes) < 10 ? `0${minutes as string}` : minutes}
            </Text>
            <Text fontSize={'16px'} color={'red'}>
              :
            </Text>
            <Text fontSize={'16px'} color={'red'}>
              {parseInt(seconds) < 10 ? `0${seconds as string}` : seconds}
            </Text>
          </Flex>
        </Flex>
      )
    }
  }

  const pauseRenderer = ({ hours, minutes, seconds, completed }: any) => {
    console.log('pause')
  }

  const setRef = (countdown: Countdown | null): void => {
    if (countdown) {
      countdownApi = countdown.getApi()
    }
  }

  const time = useMemo(() => {
    return Date.now() + COUNTDOWN
  }, [isResend])

  return (
    <Fragment>
      <Flex
        gap={5}
        flexDirection={{ base: 'column', md: 'row' }}
        alignItems={{ base: 'start', md: 'center' }}
      >
        <Flex justifyContent={'start'}>
          <Input
            label="OTP"
            {...register('otp', {
              required: 'OTP is required'
            })}
            disabled={loading}
          />
          <Button
            bg={'bg'}
            py={'31px'}
            borderRadius={0}
            isLoading={loading}
            isDisabled={loading || resending}
            _hover={{
              bg: 'bg'
            }}
            type="button"
            onClick={onSubmit}
          >
            <Text fontSize={'18px'} fontFamily={'DM Sans Regular, sans-serif'}>
              Verify
            </Text>
          </Button>
        </Flex>
        {countdown > 0 && (
          <Countdown
            date={time}
            renderer={renderer}
            onPause={pauseRenderer}
            ref={setRef}
          />
        )}
      </Flex>
      {authError && (
        <div>
          <span className="w-full text-rose-500 text-medium-regular">
            {authError}
          </span>
        </div>
      )}

      <Flex mt={-2} mb={0} flexDirection={'column'}>
        <Text color={'red'} fontSize={'16px'}>
          Did not received an OTP via Whatsapp?
        </Text>
        <Flex
          flexDirection={{ base: 'column', md: 'row' }}
          alignItems={{ base: 'start', md: 'center' }}
          justifyContent={'space-between'}
          mt={3}
        >
          <Flex gap={5}>
            <Text display={{ base: 'none', lg: 'inline' }} fontSize={'20px'}>
              Resend OTP via:
            </Text>
            <RadioGroup onChange={setChannel} value={channel}>
              <Stack direction="row" gap={3}>
                <Radio value={OTPChannel.SMS} size={'lg'}>
                  <Text fontSize={'20px'}>SMS</Text>
                </Radio>
                <Radio value={OTPChannel.WHATSAPP} size={'lg'}>
                  <Text fontSize={'20px'}>Whatsapp</Text>
                </Radio>
              </Stack>
            </RadioGroup>
          </Flex>
          <Button
            mt={{ base: 5, md: 0 }}
            bg={'bg'}
            borderRadius={0}
            isLoading={resending}
            disabled={resending}
            _hover={{
              bg: 'bg'
            }}
            type="button"
            onClick={onResendOtp}
          >
            <Text fontSize={'18px'} fontFamily={'DM Sans Regular, sans-serif'}>
              Resend
            </Text>
          </Button>
        </Flex>
      </Flex>
    </Fragment>
  )
}

export default Otp
